/**
 * Localization Service
 * Foundational multi-language support for international shipping and domestic diversity.
 */

export type SupportedLanguage = 'en' | 'hi' | 'bn' | 'th' | 'vi' | 'ar' | 'es' | 'ja';

export interface LanguageInfo {
    code: SupportedLanguage;
    name: string;
    flag: string;
    dir: 'ltr' | 'rtl';
}

export const LANGUAGES: LanguageInfo[] = [
    { code: 'en', name: 'English', flag: '🇺🇸', dir: 'ltr' },
    { code: 'hi', name: 'हिन्दी', flag: '🇮🇳', dir: 'ltr' },
    { code: 'bn', name: 'বাংলা', flag: '🇧🇩', dir: 'ltr' },
    { code: 'th', name: 'ไทย', flag: '🇹🇭', dir: 'ltr' },
    { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳', dir: 'ltr' },
    { code: 'ar', name: 'العربية', flag: '🇦🇪', dir: 'rtl' },
    { code: 'ja', name: '日本語', flag: '🇯🇵', dir: 'ltr' },
    { code: 'es', name: 'Español', flag: '🇪🇸', dir: 'ltr' }
];

const TRANSLATIONS: Record<SupportedLanguage, Record<string, string>> = {
    en: {
        'nav.dashboard': 'Dashboard',
        'nav.export': 'Global Market',
        'nav.tracking': 'Shipment Tracking',
        'btn.apply': 'Apply Now',
        'lbl.price': 'Market Price',
        'status.ready': 'Export Ready'
    },
    hi: {
        'nav.dashboard': 'डैशबोर्ड',
        'nav.export': 'वैश्विक बाजार',
        'nav.tracking': 'शिपमेंट ट्रैकिंग',
        'btn.apply': 'अभी आवेदन करें',
        'lbl.price': 'बाजार मूल्य',
        'status.ready': 'निर्यात के लिए तैयार'
    },
    ar: {
        'nav.dashboard': 'لوحة القيادة',
        'nav.export': 'السوق العالمية',
        'nav.tracking': 'تتبع الشحنة',
        'btn.apply': 'قدم الآن',
        'lbl.price': 'سعر السوق',
        'status.ready': 'جاهز للتصدير'
    },
    bn: {
        'nav.dashboard': 'ড্যাশবোর্ড',
        'nav.export': 'বৈশ্বিক বাজার',
        'nav.tracking': 'শিপমেন্ট ট্র্যাকিং',
        'btn.apply': 'এখনই আবেদন করুন',
        'lbl.price': 'বাজার মূল্য',
        'status.ready': 'রপ্তানিযোগ্য'
    },
    th: {
        'nav.dashboard': 'แผงควบคุม',
        'nav.export': 'ตลาดโลก',
        'nav.tracking': 'การติดตามการส่งสินค้า',
        'btn.apply': 'สมัครตอนนี้',
        'lbl.price': 'ราคาตลาด',
        'status.ready': 'พร้อมส่งออก'
    },
    vi: {
        'nav.dashboard': 'Bảng điều khiển',
        'nav.export': 'Thị trường toàn cầu',
        'nav.tracking': 'Theo dõi lô hàng',
        'btn.apply': 'Đăng ký ngay',
        'lbl.price': 'Giá thị trường',
        'status.ready': 'Sẵn sàng xuất khẩu'
    },
    ja: {
        'nav.dashboard': 'ダッシュボード',
        'nav.export': 'グローバル市場',
        'nav.tracking': '配送追跡',
        'btn.apply': '今すぐ申し込む',
        'lbl.price': '市場価格',
        'status.ready': '輸出準備完了'
    },
    es: {
        'nav.dashboard': 'Panel',
        'nav.export': 'Mercado Global',
        'nav.tracking': 'Seguimiento',
        'btn.apply': 'Aplicar ahora',
        'lbl.price': 'Precio de mercado',
        'status.ready': 'Listo para exportar'
    }
};

class LocalizationService {
    translate(key: string, lang: SupportedLanguage = 'en'): string {
        return TRANSLATIONS[lang]?.[key] || TRANSLATIONS['en']?.[key] || key;
    }

    getLanguageInfo(code: SupportedLanguage): LanguageInfo {
        return LANGUAGES.find(l => l.code === code) || LANGUAGES[0];
    }
}

export const localizationService = new LocalizationService();
export default localizationService;
